import { useState, useEffect, useCallback } from 'react';
import { subscriptionApi } from '../utils/subscriptionApi';

/**
 * Hook for fetching subscription plans
 */
export function usePlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const data = await subscriptionApi.getPlans();
        setPlans(data);
      } catch (err) {
        setError('Failed to load plans');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  return { plans, loading, error };
}

/**
 * Hook for user's subscription status
 */
export function useSubscriptionStatus(userId) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await subscriptionApi.getStatus(userId);
      setStatus(data);
    } catch (err) {
      setError('Failed to load subscription status');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { status, loading, error, refresh };
}

/**
 * Hook for voucher validation and activation
 */
export function useVoucher() {
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState(null);

  const validate = async (code, userId) => {
    setValidating(true);
    setError(null);
    setValidationResult(null);

    try {
      const result = await subscriptionApi.validateVoucher(code, userId);
      setValidationResult(result);
      return result;
    } catch (err) {
      const message = err.response?.data?.detail || 'Validation failed';
      setError(message);
      return { valid: false, message };
    } finally {
      setValidating(false);
    }
  };

  const activateForNewUser = async (code, user, consent) => {
    setActivating(true);
    setError(null);

    try {
      const result = await subscriptionApi.activateVoucherNewUser(code, user, consent);
      return result;
    } catch (err) {
      const message = err.response?.data?.detail || 'Activation failed';
      setError(message);
      throw new Error(message);
    } finally {
      setActivating(false);
    }
  };

  const activateForExistingUser = async (code, userId) => {
    setActivating(true);
    setError(null);

    try {
      const result = await subscriptionApi.activateVoucherExisting(code, userId);
      return result;
    } catch (err) {
      const message = err.response?.data?.detail || 'Activation failed';
      setError(message);
      throw new Error(message);
    } finally {
      setActivating(false);
    }
  };

  const reset = () => {
    setValidationResult(null);
    setError(null);
  };

  return {
    validationResult,
    validating,
    activating,
    error,
    validate,
    activateForNewUser,
    activateForExistingUser,
    reset,
  };
}

/**
 * Hook for Razorpay subscription
 */
export function useRazorpaySubscription() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const subscribe = async (userId, planId) => {
    setLoading(true);
    setError(null);

    try {
      const result = await subscriptionApi.createSubscription(userId, planId);
      
      // Open Razorpay checkout
      if (result.checkout_options && window.Razorpay) {
        const razorpay = new window.Razorpay({
          ...result.checkout_options,
          handler: async (response) => {
            // Confirm subscription on successful payment
            await subscriptionApi.confirmSubscription(
              response.razorpay_payment_id,
              response.razorpay_subscription_id,
              response.razorpay_signature
            );
          },
        });
        razorpay.open();
      }
      
      return result;
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to create subscription';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  return { subscribe, loading, error };
}
